import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../utils/auth";
import {
  loadPrograms,
  saveSubKegiatanAPI,
  updateSubKegiatan,
  summarize,
} from "../utils/programStorage";
import SummaryCards from "../components/Program/SummaryCards";
import FilterBar from "../components/Program/FilterBar";
import ProgramTable from "../components/Program/ProgramTable";
import EditSubKegiatanModal from "../components/Program/EditSubKegiatanModal";
import { Toaster, toast } from "../components/ui/sonner";

export default function Program() {
  const { user } = useAuth();
  const canEdit = hasRole(user, "IRBAN", "DALNIS");

  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [prioritas, setPrioritas] = useState("ALL");
  const [programFilter, setProgramFilter] = useState("ALL");

  const [modalOpen, setModalOpen] = useState(false);
  const [activeSub, setActiveSub] = useState(null);

  useEffect(() => {
    loadPrograms()
      .then((data) => {
        setPrograms(data);
        setLoadingPrograms(false);
      })
      .catch(() => setLoadingPrograms(false));
  }, []);

  const summary = useMemo(() => summarize(programs), [programs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return programs
      .filter((p) => (programFilter === "ALL" ? true : p.id === programFilter))
      .map((p) => {
        const subKegiatan = p.subKegiatan.filter((s) => {
          if (status !== "ALL" && s.status !== status) return false;
          if (prioritas !== "ALL" && s.prioritas !== prioritas) return false;
          if (q && !s.nama.toLowerCase().includes(q)) return false;
          return true;
        });
        return { ...p, subKegiatan };
      })
      .filter((p) => {
        // Hide programs that end up empty after sub-level filters are applied,
        // except when only a program filter is active and no sub filter removes anything.
        if (status === "ALL" && prioritas === "ALL" && !q) return true;
        return p.subKegiatan.length > 0;
      });
  }, [programs, search, status, prioritas, programFilter]);

  const openEdit = (sub) => {
    setActiveSub(sub);
    setModalOpen(true);
  };

  const handleSave = async (patch) => {
    const next = updateSubKegiatan(programs, patch.id, {
      targetMulai: patch.targetMulai,
      targetSelesai: patch.targetSelesai,
      prioritas: patch.prioritas,
      status: patch.status,
      progress: patch.progress,
      catatan: patch.catatan,
    });
    setPrograms(next);
    setModalOpen(false);
    try {
      await saveSubKegiatanAPI(patch.id, {
        targetMulai: patch.targetMulai,
        targetSelesai: patch.targetSelesai,
        prioritas: patch.prioritas,
        status: patch.status,
        progress: patch.progress,
        catatan: patch.catatan,
      });
      toast.success("Sub-kegiatan berhasil diperbarui");
    } catch (err) {
      toast.error("Gagal menyimpan ke server: " + (err?.response?.data?.detail || err.message));
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("ALL");
    setPrioritas("ALL");
    setProgramFilter("ALL");
  };

  if (loadingPrograms) {
    return (
      <div className="flex items-center justify-center py-20 text-[13px] text-slate-500">
        Memuat program…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="program-page">
      <Toaster position="top-right" richColors />

      {/* Page header */}
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Master Data
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            Program Pengawasan 2026
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            Daftar {programs.length} program pengawasan tahunan unit Irbansus — klik baris
            untuk melihat sub-kegiatan.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Akses Anda
          </div>
          <span
            data-testid="access-label"
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              canEdit
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-gray-200 bg-gray-50 text-gray-600"
            }`}
          >
            {canEdit ? "Edit" : "Hanya Baca"}
          </span>
        </div>
      </div>

      <SummaryCards summary={summary} />

      <FilterBar
        search={search}
        onSearch={setSearch}
        status={status}
        onStatus={setStatus}
        prioritas={prioritas}
        onPrioritas={setPrioritas}
        program={programFilter}
        onProgram={setProgramFilter}
        programs={programs}
        onReset={resetFilters}
      />

      <ProgramTable
        programs={filtered}
        canEdit={canEdit}
        onEditSub={openEdit}
      />

      <EditSubKegiatanModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        sub={activeSub}
        canEdit={canEdit}
        onSave={handleSave}
      />
    </div>
  );
}
